import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { BottomSheet } from 'react-spring-bottom-sheet';
import 'react-spring-bottom-sheet/dist/style.css';
import { Steps, Button, Modal } from 'antd';
import { ArrowLeftOutlined, ArrowRightOutlined, UpOutlined, CreditCardOutlined, PhoneFilled, HomeOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { Row, Col } from 'react-bootstrap';
import BoxSelection from './BoxSelection';
import PackingSupplies from './PackingSupplies';
import MovingSupplies from './MovingSupplies';
import DeliveryDetails from './DeliveryDetails';
import PickupDetails from './PickupDetails';
import OrderSummary from './OrderSummary';
import { setAlaCarteStep, resetAlaCarteStore, setTaxRate, setRentalInfo, setLockedPickupDetails, setDeliveryAddressFromRental } from './store/actionCreators';
import { getTax, getCustomerAddress, packageCustomerData, getDeliverWindows, getExtraWorkInventory, getCustomerRentalInfo } from '../../data/API';
import { message } from 'antd';
import './index.css';
import '../StorageBox/BuyingBox/index.css';

const AlaCarteOrder = (props) => {
  const tabref = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { currentStep, boxCount, boxProduct, packingSupplies, movingSupplies, pricing } = useSelector(state => state.alaCarte);

  const [orderSummaryOpen, setOrderSummaryOpen] = useState(false);
  const [isParentNextStepClicked, setParentNextStepClicked] = useState(false);
  const [isParentPayClicked, setParentPayClicked] = useState(false);
  const [customerData, setCustomerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deliveryWindows, setDeliveryWindows] = useState([]);
  const [extraWork, setExtraWork] = useState([]);

  useEffect(() => {
    // Check if user is authenticated
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/storage-box/login');
    }
  }, [navigate]);

  // Track if component is mounted to run cleanup only on unmount
  const isMountedRef = useRef(true);

  useEffect(() => {
    props.setShowHideHeader && props.setShowHideHeader(true);
    props.setshowHideFooter && props.setshowHideFooter(false);
    props.setshowHideinnerFooter && props.setshowHideinnerFooter(false);

    // Cleanup only runs when component unmounts
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Separate cleanup effect that only runs on unmount
  useEffect(() => {
    return () => {
      // Only run cleanup if we're actually unmounting (not just re-rendering)
      if (!isMountedRef.current) {
        const hasItems = boxCount > 0 || packingSupplies.length > 0 || movingSupplies.length > 0;

        if (!hasItems) {
          dispatch(resetAlaCarteStore());
          console.log('À la carte state cleared - no items selected');
        } else {
          console.log('À la carte state preserved - items selected');
        }
      }
    };
  }, [boxCount, packingSupplies, movingSupplies, dispatch]);

  useEffect(() => {
    if (boxProduct?.id) {
      getExtraWorkInfo(boxProduct.id);
    }
  }, [boxProduct]);

  // Fetch delivery windows (matching original implementation in Step4.js)
  const getDeliverySlots = async () => {
    try {
      let response = await getDeliverWindows();
      if (response?.data?.results) {
        // Use data directly without transformation - matching Step4.js line 464
        setDeliveryWindows(response.data.results);
      }
    } catch (err) {
      message.error('Error fetching delivery windows');
      console.error('Error fetching delivery windows:', err);
    }
  };

  const getExtraWorkInfo = async (productId) => {
    if (!productId) return;
    try {
      let response = await getExtraWorkInventory(productId, 'delivery');
      if (response?.data) {
        setExtraWork(response?.data.results);
      }
    } catch (err) {
      message.error('Error fetching extra work info');
      console.error('Error fetching extra work info:', err);
    }
  };

  // Fetch customer data and tax rate
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);

        getDeliverySlots();

        // Get token type to determine which API to use
        const token = localStorage.getItem('token');
        const tokenType = token ? JSON.parse(atob(token.split('.')[1])).type : 'customer';

        // Fetch customer data based on customer type
        let response;

        if (tokenType === 'customer') {
          // Storage customer - use Storage API
          response = await getCustomerAddress();
          if (response?.data?.success) {
            setCustomerData(response.data.data);
          } else {
            message.error('Failed to load customer data');
            // navigate('/storage-box/dashboard');
            return;
          }
        } else if (tokenType === 'rental_customer' || tokenType === 'both_customer') {
          // Rental or Both customer - use Django rental API
          try {
            response = await packageCustomerData();
            if (response?.data) {
              const normalizedData = normalizeCustomerData(response.data);
              setCustomerData(normalizedData);
            } else {
              message.error('Failed to load customer data');
              // navigate('/storage-box/dashboard');
              return;
            }
          } catch (apiError) {
            if (apiError.response?.status === 404) {
              console.warn("packageCustomerData endpoint not found");
              const authData = JSON.parse(localStorage.getItem('authData') || '{}');
              setCustomerData({
                id: authData.id,
                name: authData.name,
                email: authData.email,
                custormerData: {}
              });
            } else {
              throw apiError;
            }
          }
        }

        // Fetch tax rate
        const storedTax = localStorage.getItem('tax');
        if (storedTax) {
          dispatch(setTaxRate(parseFloat(storedTax)));
        } else {
          const res = await getTax();
          const fetchedTax = res?.data?.results[0]?.tax;
          if (fetchedTax) {
            dispatch(setTaxRate(fetchedTax));
            localStorage.setItem('tax', fetchedTax);
          }
        }

        let customerId = null;
        if (tokenType === 'customer') {
          customerId = response?.data?.data?.id;
        } else {
          customerId = response?.data?.id || response?.data?.data?.id;
        }

        if (customerId) {
          try {
            const rentalInfoResponse = await getCustomerRentalInfo(customerId);
            if (rentalInfoResponse?.data?.success) {
              const rentalData = rentalInfoResponse.data;
              dispatch(setRentalInfo(rentalData));
              console.log('Rental period loaded:', rentalData.rental_period);

              if (rentalData.pickup_details) {
                dispatch(setLockedPickupDetails(rentalData.pickup_details));
                console.log('Locked pickup details loaded from rental order');
              }

              if (rentalData.delivery_address) {
                dispatch(setDeliveryAddressFromRental(rentalData.delivery_address));
                console.log('Delivery address loaded from rental order');
              }
            } else {
              // No active rental order found - show modal
              const errorMessage = rentalInfoResponse?.data?.message ||
                'You don\'t have any active rental order. À la carte orders are only available during active rentals. Please place a new package order first.';

              Modal.warning({
                title: 'No Active Rental Order',
                icon: <ExclamationCircleOutlined />,
                content: errorMessage,
                okText: 'Go to Packages',
                onOk: () => {
                  navigate('/packages');
                },
                onCancel: () => {
                  navigate('/my-account');
                },
                cancelText: 'Back to Dashboard',
                closable: true,
                maskClosable: true
              });
              console.warn('No active rental order found:', errorMessage);
            }
          } catch (rentalError) {
            // API error - check if it's a 400 (inactive order) or other error
            if (rentalError.response?.status === 400 || rentalError.response?.status === 404) {
              const errorMessage = rentalError.response?.data?.message ||
                'You don\'t have any active rental order. À la carte orders are only available during active rentals. Please place a new package order first.';

              Modal.warning({
                title: 'No Active Rental Order',
                icon: <ExclamationCircleOutlined />,
                content: errorMessage,
                okText: 'Go to Packages',
                onOk: () => {
                  navigate('/packages');
                },
                onCancel: () => {
                  navigate('/my-account');
                },
                cancelText: 'Back to Dashboard',
                closable: true,
                maskClosable: true
              });
            } else {
              console.warn('Could not fetch rental info:', rentalError);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching initial data:', error);
        message.error('Failed to load required data. Please try again.');
        // navigate('/storage-box/dashboard');
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [dispatch, navigate]);

  const normalizeCustomerData = (data) => {
    if (data.custormerData && !data.data) {
      return data;
    }

    if (data.data && data.data.custormerData) {
      return {
        id: data.data.id,
        name: data.data.first_name ? `${data.data.first_name} ${data.data.last_name}` : data.data.name,
        email: data.data.email,
        lastLoggedIn: data.data.lastLoggedIn,
        phone_number: data.data.phone_number,
        secondary_phone_number: data.data.secondary_phone_number,
        company_name: data.data.company_name,
        token: data.data.token,
        refreshToken: data.data.refreshToken,
        custormerData: data.data.custormerData
      };
    }

    return data;
  };

  const isPackingOnly = boxCount === 0 && movingSupplies.length === 0;
  const validationSuccessRef = useRef(false);

  const nextStep = () => {
    setParentNextStepClicked(true);

    setTimeout(() => {
      setParentNextStepClicked(false);

      // Check if current step is Delivery Details (step 3, index 3)
      // Only block navigation if validation failed on Delivery Details
      const isDeliveryDetailsStep = currentStep === 3;

      if (isDeliveryDetailsStep) {
        // Only proceed if validation succeeded
        if (validationSuccessRef.current) {
          dispatch(setAlaCarteStep(currentStep + 1));
        }
      } else {
        // For other steps (Box, Packing, Moving), always proceed
        dispatch(setAlaCarteStep(currentStep + 1));
      }
    }, 100);
  };

  const payOrder = async () => {
    setParentPayClicked(true);
  };

  const prevStep = () => {
    dispatch(setAlaCarteStep(currentStep - 1));
  };

  // const handleBackToDashboard = () => {
  //   dispatch(resetAlaCarteStore());
  //   navigate('/storage-box/dashboard');
  // };

  const allSteps = [
    {
      title: 'Box Selection',
      content: <BoxSelection
        isParentNextStepClicked={isParentNextStepClicked}
        setParentNextStepClicked={setParentNextStepClicked}
        tabref={tabref}
      />,
    },
    {
      title: 'Packing Supplies',
      content: <PackingSupplies
        isParentNextStepClicked={isParentNextStepClicked}
        setParentNextStepClicked={setParentNextStepClicked}
        tabref={tabref}
      />,
    },
    {
      title: 'Moving Supplies',
      content: <MovingSupplies
        isParentNextStepClicked={isParentNextStepClicked}
        setParentNextStepClicked={setParentNextStepClicked}
        tabref={tabref}
      />,
    },
    {
      title: 'Delivery Details',
      content: <DeliveryDetails
        isParentNextStepClicked={isParentNextStepClicked}
        setParentNextStepClicked={setParentNextStepClicked}
        isParentPayClicked={isParentPayClicked}
        setParentPayClicked={setParentPayClicked}
        validationSuccessRef={validationSuccessRef}
        customerData={customerData}
        deliveryWindows={deliveryWindows}
        extraWork={extraWork}
        tabref={tabref}
        isPackingOnly={isPackingOnly}
      />,
    },
  ];

  // Only add pickup step if NOT packing-only (boxes or moving supplies need to be returned)
  if (!isPackingOnly) {
    allSteps.push({
      title: 'Pickup Details',
      content: <PickupDetails
        isParentNextStepClicked={isParentNextStepClicked}
        setParentNextStepClicked={setParentNextStepClicked}
        isParentPayClicked={isParentPayClicked}
        setParentPayClicked={setParentPayClicked}
        customerData={customerData}
        deliveryWindows={deliveryWindows}
        extraWork={extraWork}
        tabref={tabref}
      />,
    });
  }

  const steps = allSteps;

  const items = steps.map((item) => ({
    key: item.title,
    title: item.title
  }));

  const showOrderSummary = () => {
    setOrderSummaryOpen(true);
  };

  const hideOrderSummary = () => {
    setOrderSummaryOpen(false);
  };

  const onStepChange = (value) => {
    if (value < currentStep) {
      dispatch(setAlaCarteStep(value));
    }
  };

  if (loading || !customerData) {
    return (
      <div className="my-4 text-center" style={{ padding: '50px' }}>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="my-4">
      <div className="info mr-3 text-end">
        <div>
          <h6>Need Help or Have a Question?</h6>
          <div className="info-call">
            <div className="call-label">
              <PhoneFilled />
            </div>
            <div className="call-details">
              <p className="call-text">Call or text</p>
              <p className="phone-no">(650) 502-9522</p>
            </div>
          </div>
        </div>
      </div>

      <Row className="steps-section">
        <Col>
          <div className="w-100" style={{ marginTop: '0vh' }} ref={tabref}>
            <div className="steps-layout">
              <Steps
                responsive={false}
                current={currentStep}
                labelPlacement="vertical"
                onChange={onStepChange}
                items={steps.map((item, index) => ({
                  key: index,
                  title: (
                    <div>
                      <div className="step-title-text">{item.title}</div>
                      <div className="step-progress-indicator d-block d-sm-none">(Step {index + 1}/5)</div>
                    </div>
                  ),
                  disabled: currentStep < index
                }))}
              />
              <div className="container-fluid pb-2 pt-0 mt-4">
                <div className="row">
                  <div className="col px-1 d-flex justify-content-start align-items-center">
                    <Button
                      className={`btn-previous-step red-border-button ${currentStep > 0 ? '' : 'd-none'}`}
                      icon={<ArrowLeftOutlined />}
                      size="large"
                      onClick={prevStep}
                    >
                      Previous Step
                    </Button>
                  </div>
                  {
                    // Show "Pay & Order" on last step
                    // Last step is 3 (Delivery) for packing-only, or 4 (Pickup) for rental items
                    currentStep === (isPackingOnly ? 3 : 4) ? (
                      <div className="col px-1 d-flex justify-content-end align-items-center">
                        <Button
                          className="btn-next-step red-button"
                          icon={<CreditCardOutlined />}
                          size="large"
                          onClick={payOrder}
                        >
                          Pay & Order
                        </Button>
                      </div>
                    ) : (
                      <div className="col px-1 d-flex justify-content-end align-items-center">
                        <Button
                          className="btn-next-step red-button"
                          icon={<ArrowRightOutlined />}
                          size="large"
                          onClick={nextStep}
                        >
                          Next Step
                        </Button>
                      </div>
                    )
                  }
                </div>
              </div>
            </div>
          </div>
        </Col>
      </Row>

      <Row className="row d-flex flex-row">
        <div className="col-lg-8 col-md-12 col-sm-12 col-xs-12">
          <section className="pb-0 mb-4 pl-3 pr-3">
            <div className="container">
              <div className="row d-flex justify-content-end">
                <div className="col-lg-11 p-0 step-shadow">
                  {steps[currentStep].content}
                  <div className="row mt-4 justify-content-between">
            <div className="col d-flex justify-content-start align-items-center">
              <Button
                className={`btn-previous-step red-border-button ${currentStep > 0 ? '' : 'd-none'}`}
                icon={<ArrowLeftOutlined />}
                onClick={prevStep}
                size={'large'}>
                Previous Step
              </Button>
            </div>
            <div className="col d-flex justify-content-end align-items-center">
              {currentStep === (isPackingOnly ? 3 : 4) ? (
                <Button
                  className="btn-next-step red-button"
                  icon={<CreditCardOutlined />}
                  size={'large'}
                  onClick={payOrder}
                >
                  Pay & Order
                </Button>
              ) : (
                <Button
                  className="btn-next-step red-button"
                  icon={<ArrowRightOutlined />}
                  size={'large'}
                  onClick={nextStep}
                >
                  Next Step
                </Button>
              )}
            </div>
          </div>
                </div>

          
              </div>
            </div>
          </section>

          <div style={{ height: '200px', width: '100%' }}></div>
        </div>

        <div className="col-lg-3 col-md-12 col-sm-12 col-xs-12 p-0 order-summary mb-10">
          <section className="pb-0 order-summary-section">
            <div className="container order-summary-container pr-0">
              <div className="row d-flex mr-0">
                <div className="col-lg-11 p-0 bg-white summary-card order-summary-desktop step-shadow">
                  <OrderSummary
                    setOrderSummaryOpen={setOrderSummaryOpen}
                    deliveryWindows={deliveryWindows}
                  />
                </div>
                <div className="order-summary-mt">
                  <Button
                    icon={<UpOutlined />}
                    onClick={showOrderSummary}
                    className="btn bg-theme-secondary order-summary-mt-btn"
                  >
                    Order Summary
                  </Button>

                  <BottomSheet open={orderSummaryOpen} blocking={false} data-body-scroll-lock-ignore="true">
                    <div className="order-summary-mt-header" onClick={hideOrderSummary}>
                      <p className="mb-0 ml-4 pt-3 pb-3 title">
                        <i className="fa fa-chevron-down mr-2"></i> Order Summary
                      </p>
                    </div>
                    <OrderSummary
                      setOrderSummaryOpen={setOrderSummaryOpen}
                      deliveryWindows={deliveryWindows}
                    />
                  </BottomSheet>
                </div>
              </div>
            </div>
          </section>
        </div>
      </Row>
    </div>
  );
};

AlaCarteOrder.propTypes = {
  setShowHideHeader: PropTypes.func,
  setshowHideFooter: PropTypes.func,
  setshowHideinnerFooter: PropTypes.func
};

export default AlaCarteOrder;
